import type {
    EmailAiDraftAdapter,
    EmailAiDraftGeneratePayload,
    EmailAiDraftUpdatePayload,
} from '@/components/lead_xpress/aiDraft/types.ts';
import { plainTextToComposerHtml } from '@/components/lead_xpress/aiDraft/draftBodyHtml.ts';

const MOCK_BODY_BY_LOCALE: Record<string, string> = {
    en: '<p>Hi Michael Chen,<br><br>Just following up again after your test drive of the Toyota Rav 4.<br><br>If you are still considering it, I can send a refreshed offer, confirm availability, or put together a quick trade-in estimate.<br><br>Best,<br>Priyanka Dhapare<br>BDC Agent</p>',
    fr: '<p>Bonjour Michael Chen,<br><br>Je fais un suivi après votre essai routier du Toyota Rav 4.<br><br>Je peux vous envoyer une offre mise à jour, confirmer la disponibilité ou préparer une estimation de reprise.<br><br>Merci,<br>Priyanka Dhapare<br>BDC Agent</p>',
};

const MOCK_UPDATE_INTRO_BY_LOCALE: Record<string, string> = {
    en: 'Updated with AI based on your note:',
    fr: "Mis à jour avec l'IA selon votre note :",
};

const MOCK_GENERATE_DELAY_MS = 3000;
const MOCK_UPDATE_DELAY_MS = 3000;
const MOCK_USAGE_LIMIT = 3;
const MOCK_USAGE_WINDOW_MS = 60_000;

let requestTimestamps: number[] = [];

const delay = (milliseconds: number) =>
    new Promise((resolve) => {
        globalThis.setTimeout(resolve, milliseconds);
    });

const enforceMockUsageLimit = () => {
    const now = Date.now();
    requestTimestamps = requestTimestamps.filter(
        (timestamp) => now - timestamp < MOCK_USAGE_WINDOW_MS
    );

    if (requestTimestamps.length >= MOCK_USAGE_LIMIT) {
        const oldestRequest = requestTimestamps[0];
        const error = new Error('AI usage limit reached') as Error & {
            rateLimitSeconds: number;
        };
        error.rateLimitSeconds = Math.max(
            1,
            Math.ceil((MOCK_USAGE_WINDOW_MS - (now - oldestRequest)) / 1000)
        );
        throw error;
    }

    requestTimestamps.push(now);
};

const HTML_ESCAPE_MAP: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
};

const escapeHtml = (value: string) =>
    value.replaceAll(/[&<>"']/g, (character) => HTML_ESCAPE_MAP[character]);

const buildUpdatedBody = ({ body, instruction, locale }: EmailAiDraftUpdatePayload) => {
    const trimmedInstruction = instruction.trim();
    const intro = MOCK_UPDATE_INTRO_BY_LOCALE[locale] ?? MOCK_UPDATE_INTRO_BY_LOCALE.en;

    return [
        `<p>${intro}</p>`,
        `<blockquote>${escapeHtml(trimmedInstruction)}</blockquote>`,
        plainTextToComposerHtml(body),
    ].join('');
};

export const mockEmailAiDraftAdapter: EmailAiDraftAdapter = {
    async generate(payload: EmailAiDraftGeneratePayload) {
        enforceMockUsageLimit();
        await delay(MOCK_GENERATE_DELAY_MS);

        return {
            subject:
                payload.locale === 'fr'
                    ? 'Suivi de votre essai routier'
                    : 'Following up on your test drive',
            body: MOCK_BODY_BY_LOCALE[payload.locale] ?? MOCK_BODY_BY_LOCALE.en,
            interactionId: 0,
            traceId: null,
        };
    },

    async update(payload: EmailAiDraftUpdatePayload) {
        enforceMockUsageLimit();
        await delay(MOCK_UPDATE_DELAY_MS);

        return {
            body: buildUpdatedBody(payload),
            interactionId: 0,
            traceId: null,
        };
    },

    async stop() {
        await delay(50);
    },

    async submitFeedback() {
        await delay(50);
    },
};
